import { MapContainer, TileLayer, useMap } from 'react-leaflet'
import { useEffect, memo, Fragment, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { Runner, LegAssignment, LegLog, LocationPoint } from '../../types/index'
import { RunnerMarker } from './RunnerMarker'
import { VanMarker } from './VanMarker'
import { RouteOverlay } from './RouteOverlay'
import { HandoffCircle } from './HandoffCircle'
import { ExchangePointMarkers } from './ExchangePointMarkers'
import { VanRoute } from './VanRoute'
import type { VanRouteMode } from './VanRoute'
import { useNightMode } from '../../hooks/useNightMode'

interface MapViewProps {
  runners: Runner[]
  legs: LegAssignment[]
  logs?: LegLog[]
  currentRunner: Runner | null | undefined
  currentLegNumber: number
  locations: Record<number, LocationPoint>
  projectedLocations?: Record<number, LocationPoint>
  flyToCurrentLegTrigger?: number
  flyToFullCourseTrigger?: number
}

function FlyToCurrentLeg({ currentLeg, trigger }: { currentLeg: LegAssignment | undefined, trigger: number }) {
  const map = useMap()
  useEffect(() => {
    if (trigger === 0 || !currentLeg) return
    const bounds = L.latLngBounds(
      [currentLeg.startLat, currentLeg.startLng],
      [currentLeg.endLat, currentLeg.endLng],
    )
    map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 14, duration: 0.8 })
  }, [trigger]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

function FlyToFullCourse({ legs, trigger }: { legs: LegAssignment[], trigger: number }) {
  const map = useMap()
  useEffect(() => {
    if (trigger === 0 || legs.length === 0) return
    const coords = legs.flatMap(leg => [
      [leg.startLat, leg.startLng] as L.LatLngTuple,
      [leg.endLat, leg.endLng] as L.LatLngTuple,
    ])
    // invalidateSize first so Leaflet knows the true container dimensions before fitting
    map.invalidateSize()
    map.fitBounds(L.latLngBounds(coords), { padding: [30, 30] })
  }, [trigger]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

function FitBounds({ legs }: { legs: LegAssignment[] }) {
  const map = useMap()
  useEffect(() => {
    if (legs.length === 0) return
    const coords = legs.flatMap(leg => [
      [leg.startLat, leg.startLng] as L.LatLngTuple,
      [leg.endLat, leg.endLng] as L.LatLngTuple,
    ])
    const bounds = L.latLngBounds(coords)
    map.invalidateSize()
    map.fitBounds(bounds, { paddingTopLeft: [30, 30], paddingBottomRight: [30, 30] })
  }, [legs, map])
  return null
}

export const MapView = memo(function MapView({
  runners,
  legs,
  logs = [],
  currentRunner,
  currentLegNumber,
  locations,
  projectedLocations = {},
  flyToCurrentLegTrigger = 0,
  flyToFullCourseTrigger = 0,
}: MapViewProps) {
  const currentLeg = legs.find(l => l.legNumber === currentLegNumber)
  const [vanRouteMode, setVanRouteMode] = useState<VanRouteMode>('off')
  const { nightMode, toggleNightMode } = useNightMode()

  const cycleVanRoute = () =>
    setVanRouteMode(m => m === 'off' ? 'current' : m === 'current' ? 'all' : 'off')

  const courseBounds = legs.length > 0
    ? L.latLngBounds(legs.flatMap(l => [[l.startLat, l.startLng], [l.endLat, l.endLng]] as L.LatLngTuple[]))
    : L.latLngBounds([[38.9, -120.3], [39.6, -119.6]])

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        bounds={courseBounds}
        boundsOptions={{ padding: [30, 30] }}
        style={{ width: '100%', height: '100%' }}
        maxBounds={L.latLngBounds([37.5, -121.0], [41.0, -118.0])}
      >
      {currentLegNumber === 0 && <FitBounds legs={legs} />}
      <FlyToCurrentLeg currentLeg={currentLeg} trigger={flyToCurrentLegTrigger} />
      <FlyToFullCourse legs={legs} trigger={flyToFullCourseTrigger} />
      {nightMode ? (
        <TileLayer
          key="dark"
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
          maxNativeZoom={19}
          maxZoom={19}
        />
      ) : (
        <TileLayer
          key="light"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxNativeZoom={19}
          maxZoom={19}
        />
      )}

      {/* Van route overlay */}
      <VanRoute mode={vanRouteMode} currentLeg={currentLeg} />

      {/* Route overlay with color-coded legs */}
      <RouteOverlay
        legs={legs}
        logs={logs}
        runners={runners}
        currentLegNumber={currentLegNumber}
      />

      {/* Exchange point markers (XP 1–35) and Start/Finish star */}
      <ExchangePointMarkers legs={legs} logs={logs} runners={runners} currentLegNumber={currentLegNumber} />

      {/* 20ft handoff circle — always at actual GPS position */}
      {currentRunner && locations[currentRunner.id] && (
        <HandoffCircle
          center={{
            lat: locations[currentRunner.id].latitude,
            lng: locations[currentRunner.id].longitude,
          }}
        />
      )}

      {/* Current runner: estimated marker always shown; real GPS shown when fresh */}
      {currentRunner && projectedLocations[currentRunner.id] && (
        <RunnerMarker
          runner={currentRunner}
          location={projectedLocations[currentRunner.id]}
          isCurrent={true}
          isProjected={true}
        />
      )}
      {currentRunner && locations[currentRunner.id] && (
        <RunnerMarker
          runner={currentRunner}
          location={locations[currentRunner.id]}
          isCurrent={true}
          isProjected={false}
        />
      )}

      {/* Other running runners: estimated + real GPS when fresh */}
      {runners
        .filter(r => r.id !== currentRunner?.id && r.status === 'running')
        .map(runner => (
          <Fragment key={runner.id}>
            {projectedLocations[runner.id] && (
              <RunnerMarker runner={runner} location={projectedLocations[runner.id]} isCurrent={false} isProjected={true} />
            )}
            {locations[runner.id] && (
              <RunnerMarker runner={runner} location={locations[runner.id]} isCurrent={false} isProjected={false} />
            )}
          </Fragment>
        ))}

      {/* Van markers for waiting runners */}
      <VanMarker
        vanNumber={1}
        runners={runners.filter(r => r.vanNumber === 1)}
        locations={locations}
        currentRunnerId={currentRunner?.id}
      />
      <VanMarker
        vanNumber={2}
        runners={runners.filter(r => r.vanNumber === 2)}
        locations={locations}
        currentRunnerId={currentRunner?.id}
      />
      </MapContainer>

      {/* Night mode toggle — bottom right, above Van Route button */}
      <button
        onClick={toggleNightMode}
        style={{ position: 'absolute', bottom: '60px', right: '16px', zIndex: 1000 }}
        className={`flex items-center justify-center gap-1.5 w-28 py-2 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
          nightMode
            ? 'bg-indigo-900 text-indigo-100 border-indigo-700'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 active:bg-gray-100'
        }`}
      >
        {nightMode ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m8.66-9H21M3 12H2m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
        {nightMode ? 'Day Mode' : 'Night Mode'}
      </button>

      {/* Van route cycle button — bottom right, aligned with Full Course button */}
      <button
        onClick={cycleVanRoute}
        style={{ position: 'absolute', bottom: '16px', right: '16px', zIndex: 1000 }}
        className={`flex items-center justify-center gap-1.5 w-28 py-2 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
          vanRouteMode !== 'off'
            ? 'bg-purple-800 text-white border-purple-900'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 active:bg-gray-100'
        }`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="5" cy="5" r="2" />
          <circle cx="19" cy="19" r="2" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 7v6h14v4" />
        </svg>
        {vanRouteMode === 'off' ? 'Van Route' : vanRouteMode === 'current' ? 'Van: Leg' : 'Van: All'}
      </button>
    </div>
  )
})
